import type { CanonicalEvidenceCitation, OKFRecord, OKFContext, OKFContextRequest, OKFNote } from '@aide/platform-types'

const API_BASE = '/api'

export interface WorkspacePullRequest {
  pr_id: string
  repo_id: string
  tenant_id: string
  title: string
  description: string
  source_branch: string
  target_branch: string
  status: string
  created_by: string
  created_at: string
  updated_at: string
  merged_at?: string | null
  merge_commit?: string | null
}

export interface WorkspaceSshKey {
  key_id: string
  title: string
  public_key: string
  fingerprint: string
  created_at: string
  created_by: string
}

export interface GitTreeEntry {
  name: string
  path: string
  type: 'blob' | 'tree'
  size?: number
}

export interface WorkspaceRepoSummary {
  repo_id: string
  name: string
  git_repo_name: string
  canonical_path: string
  tenant_id: string
  description: string
  visibility: string
  default_branch: string
  http_clone_url: string
  ssh_clone_url: string
  branch_count: number
  commit_count: number
  forked_from?: string | null
  created_at: string
  updated_at: string
}

export interface WorkspacePublicRepoSummary {
  repo_id: string
  owner: string
  name: string
  full_name: string
  git_repo_name: string
  canonical_path: string
  tenant_id: string
  description: string
  visibility: string
  default_branch: string
  language: string
  graph_enabled: boolean
  http_clone_url: string
  ssh_clone_url: string
  branch_count: number
  commit_count: number
  visit_count: number
  updated_at: string
}

export interface WorkspaceRepoDetail extends WorkspaceRepoSummary {
  branches: string[]
  pull_requests: WorkspacePullRequest[]
}

export interface WorkspaceServiceConfig {
  service_id: string
  service_name: string
  git_provider: string
  repository_url_kind: string
  http_clone_base: string
  ssh_clone_base: string
  supports_signup: boolean
  supports_fork: boolean
  supports_import: boolean
  supports_pull_requests: boolean
  supports_ssh_keys: boolean
  supports_https_clone: boolean
  supports_ssh_clone: boolean
  standalone_ready: boolean
  docs_url?: string | null
}

export interface WorkspacePolicyDescriptor {
  scope: string
  managed_by: string
  required_roles: string[]
  keycloak_source: string
  isolation: string
}

export interface WorkspacePersonalSettings {
  user_id: string
  email: string
  display_name: string
  preferred_clone_protocol: string
  default_landing_page: string
  ssh_key_count: number
  roles: string[]
  policy: WorkspacePolicyDescriptor
}

export interface WorkspaceOrganizationSettings {
  tenant_id: string
  org_display_name: string
  allow_signup: boolean
  default_repo_visibility: string
  require_pull_request_reviews: boolean
  allow_public_repositories: boolean
  allow_remote_imports: boolean
  blocked_import_hosts: string[]
  import_retention_mode: string
  allowed_clone_protocols: string[]
  products: string[]
  policy: WorkspacePolicyDescriptor
}

export interface WorkspaceRepoSettings {
  repo_id: string
  name: string
  description: string
  visibility: string
  default_branch: string
  homepage_url: string
  documentation_path: string
  branch_protection_enabled: boolean
  require_pull_request_reviews: boolean
  policy: WorkspacePolicyDescriptor
}

export interface WorkspaceRepoMember {
  user_id: string
  email: string
  display_name: string
  role: 'owner' | 'maintainer' | 'writer' | 'reader'
  managed_by: string
  source: string
}

export interface WorkspaceAccountSummary {
  account_id: string
  user_id: string
  email: string
  display_name: string
}

export interface WorkspaceAccountOrganization {
  organization_id: string
  name: string
  description: string
  role: string
  team_count: number
}

export interface WorkspaceAccountTeam {
  team_id: string
  organization_id: string
  name: string
  description: string
  role: string
  member_count: number
}

export interface WorkspaceAccountInvitation {
  invitation_id: string
  email: string
  role: string
  organization_id?: string | null
  team_id?: string | null
  status: string
  invited_by: string
  invited_at: string
}

export interface WorkspaceAccountContext {
  account: WorkspaceAccountSummary
  organizations: WorkspaceAccountOrganization[]
  teams: WorkspaceAccountTeam[]
  invitations: WorkspaceAccountInvitation[]
  policy: WorkspacePolicyDescriptor
}

export interface WorkspaceContextfulAccess {
  status: 'inactive' | 'trial' | 'active' | 'limit_reached'
  user_id: string
  email: string
  display_name: string
  signup_url: string
  upgrade_url: string
  free_request_limit: number
  free_requests_remaining: number
  requests_used: number
  counted_as_contextful_user: boolean
  message: string
}

export interface WorkspaceFileMutationResult {
  ok: boolean
  path: string
  branch: string
  commit: string
}

export interface WorkspaceGitHttpCredential {
  username: string
  password: string
  created_at: string
  rotated_at: string
}

export interface WorkspaceImportPlan {
  schema_version: string
  tenant_id: string
  source_url: string
  source_host: string
  provider: string
  requested_name?: string | null
  allowed: boolean
  policy_decision: { allowed: boolean; rule: string; reason: string }
  copy_strategy: string
  relay_mode: string
  remote: {
    refs: Array<{ name: string; sha?: string }>
    default_branch: string
    estimated_pack_bytes: number
    inspected: boolean
    inspection_error?: string | null
  }
  storage: { mode: string; tenant_isolated: boolean; server_persistent_source_copy: boolean }
  sensitivity: { label: string; scan: string; raw_content_accessed: boolean }
  lfs: { detected: boolean; objects: number; bytes: number }
  checks: Array<Record<string, unknown>>
}

export interface WorkspaceRemoteImportResult {
  repo: WorkspaceRepoSummary
  import_plan: WorkspaceImportPlan
  evidence: { schema_version: string; claims: Array<Record<string, unknown>>; raw_source_content_embedded: boolean }
}

export interface WorkspaceCodeIntelligenceEngine {
  engine: string
  role: string
  status: 'ready' | 'partial' | 'unavailable'
  languages: string[]
  confidence: number
  produced_facts: number
  produced_relationships: number
}

export interface WorkspaceCodeIntelligenceFact {
  fact_id: string
  fact_type: string
  name: string
  path?: string | null
  language?: string | null
  engine: string
  confidence: number
  evidence_ref: string
  attributes: Record<string, string | number | boolean>
  ontology?: {
    node_type: string
    stable_id: string
    layer: string
    properties: Record<string, string | number | boolean>
  } | null
}

export interface WorkspaceCodeIntelligenceRelationship {
  relationship_id: string
  source_fact_id: string
  target_fact_id: string
  relationship_type: string
  engine: string
  confidence: number
  evidence_refs: string[]
  ontology?: {
    edge_type: string
    source_fact_id: string
    target_fact_id: string
    direction: string
    layer: string
    properties: Record<string, string | number | boolean>
  } | null
}

export interface WorkspaceCodeIntelligenceModel {
  schema_version: string
  corpus_id: string
  tenant_id: string
  project_id: string
  scenario: string
  repositories: number
  branches: number
  files: number
  modules: number
  symbols: number
  dependencies: number
  interfaces: number
  config_references: number
  build_systems: number
  generated_artifacts: number
  confidence: number
  engine_reconciliation: number
  engines: WorkspaceCodeIntelligenceEngine[]
  facts_preview: WorkspaceCodeIntelligenceFact[]
  relationships_preview: WorkspaceCodeIntelligenceRelationship[]
  graph_schema?: {
    schema_version: string
    ontology_packs: string[]
    node_types: string[]
    edge_types: string[]
    filetype_node_type_map: Record<string, string>
    language_symbol_type_map: Record<string, string>
    relationship_type_map: Record<string, string>
    inverted_relationship_types: string[]
    normalization_policy: string
  } | null
  governance: Record<string, string | number | boolean>
}

export interface WorkspaceCodeIntelligenceRecord {
  model_id: string
  tenant_id: string
  project_id: string
  repo_id: string
  corpus_id: string
  scenario: string
  model: WorkspaceCodeIntelligenceModel
  source_ref: Record<string, string | number | boolean | null>
  created_at: string
  updated_at: string
}

export interface ForgeKnowledgeNode {
  node_id: string
  project_id: string
  tenant_id: string
  owner_user_id: string
  node_type: string
  title: string
  content: string
  status: string
  confidence: number
  visibility: string
  source_type: string
  shared_with_id: string | null
  source_conversation_id: string | null
  source_message_id: string | null
  source_session_id: string | null
  tags: string[]
  created_at: string
  updated_at: string
}

export interface ForgeKnowledgeEdge {
  edge_id: string
  source_node_id: string
  target_node_id: string
  edge_type: string
  project_id: string
  tenant_id: string
  weight: number
  metadata: Record<string, unknown>
  created_at: string
}

export interface WorkspaceCodeIntelligenceSummary {
  model_id: string
  schema_version: string
  corpus_id: string
  tenant_id: string
  project_id: string
  repo_id: string
  scenario: string
  confidence: number
  engine_reconciliation: number
  graph_schema_version?: string | null
  facts: number
  relationships: number
  engines: WorkspaceCodeIntelligenceEngine[]
  navigation_refs: Array<{
    fact_id: string
    fact_type: string
    name: string
    path?: string | null
    language?: string | null
    confidence: number
    evidence_ref: string
    ontology?: Record<string, unknown> | null
  }>
  raw_source_content_included: boolean
  updated_at: string
}

export interface WorkspacePullRequestEvidence {
  schema_version: string
  tenant_id: string
  repo_id: string
  pr_id: string
  pull_request: WorkspacePullRequest
  commits: Array<Record<string, unknown>>
  diff: { base: string; head: string; summary: string }
  ci: { status: string; evidence_refs: Array<Record<string, unknown>> }
  agent_checkpoints: Array<Record<string, unknown>>
  review_approvals: Array<Record<string, unknown>>
  code_intelligence?: WorkspaceCodeIntelligenceSummary
  code_intelligence_model?: WorkspaceCodeIntelligenceModel
  evidence_refs: Array<{ kind: string; label: string; locator: Record<string, unknown> }>
  canonical_citations: RawCanonicalEvidenceCitation[]
  citations: CanonicalEvidenceCitation[]
  policy_decisions: Array<{ allowed: boolean; rule: string; reason: string }>
  raw_transcripts_exposed: boolean
}

interface RawCanonicalEvidenceCitation {
  citation_id: string
  evidence_ref: string
  source_system: string
  source_type: string
  locator: string
  storage_mode: string
  content_hash?: string | null
  support_status: CanonicalEvidenceCitation['supportStatus']
  reason: string
}

function toCanonicalCitation(raw: RawCanonicalEvidenceCitation): CanonicalEvidenceCitation {
  return {
    citationId: raw.citation_id,
    evidenceRef: raw.evidence_ref,
    sourceSystem: raw.source_system,
    sourceType: raw.source_type,
    locator: raw.locator,
    storageMode: raw.storage_mode,
    contentHash: raw.content_hash,
    supportStatus: raw.support_status,
    reason: raw.reason,
  }
}

export interface WorkspaceCommitIntent {
  schema_version: string
  tenant_id: string
  repo_id: string
  commit: Record<string, unknown>
  validation_state: string
  why: string
  evidence_refs: Array<{ kind: string; label: string; locator: Record<string, unknown> }>
  policy_decisions: Array<{ allowed: boolean; rule: string; reason: string }>
  raw_transcripts_exposed: boolean
}

export interface WorkspaceBranchContextBundle {
  schema_version: string
  bundle_id: string
  tenant_id: string
  repo_id: string
  repo_name: string
  branch: string
  purpose: string
  task: string
  allowed_tools: string[]
  allowed_models: string[]
  evidence_refs: Array<{ kind: string; label: string; locator: Record<string, unknown> }>
  policy_decisions: Array<{ allowed: boolean; rule: string; reason: string }>
  raw_content_included: boolean
  created_at: string
}

class ApiClient {
  private token: string | null = null

  setToken(token: string | null) {
    this.token = token
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers || {})
    headers.set('Content-Type', headers.get('Content-Type') || 'application/json')
    if (this.token) headers.set('Authorization', `Bearer ${this.token}`)
    const response = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers,
      credentials: 'include',
    })
    if (!response.ok) {
      const text = await response.text()
      throw new Error(text || `Request failed: ${response.status}`)
    }
    if (response.status === 204) return undefined as T
    return response.json() as Promise<T>
  }

  listContextProjects() { return this.request<{ projects: Array<{ project_id: string; name: string }> }>('/projects?limit=500') }
  listSourceKnowledge(projectId: string, kind: 'source' | 'note', offset = 0) {
    return this.request<{ records: OKFRecord[] }>(`/knowledge/okf/records?${new URLSearchParams({ project_id: projectId, kind, offset: String(offset) })}`)
  }
  readSourceNote(projectId: string, noteId: string) {
    return this.request<OKFNote & { author_id: string }>(`/knowledge/okf/notes/${encodeURIComponent(noteId)}?project_id=${encodeURIComponent(projectId)}`)
  }
  prepareSourceContext(projectId: string, request: OKFContextRequest) {
    return this.request<OKFContext>(`/knowledge/okf/context?project_id=${encodeURIComponent(projectId)}`, { method: 'POST', body: JSON.stringify(request) })
  }

  listWorkspaceRepos() { return this.request<WorkspaceRepoSummary[]>('/repos/workspace') }
  searchPublicWorkspaceRepos(query = '') { return this.request<WorkspacePublicRepoSummary[]>(`/repos/workspace/public?${new URLSearchParams({ query }).toString()}`) }
  trackPublicWorkspaceRepoVisit(repoId: string) { return this.request<{ repo_id: string; visit_count: number }>(`/repos/workspace/public/${repoId}/visit`, { method: 'POST' }) }
  getWorkspaceServiceConfig() { return this.request<WorkspaceServiceConfig>('/repos/workspace/service-config') }
  getWorkspacePersonalSettings() { return this.request<WorkspacePersonalSettings>('/repos/workspace/settings/personal') }
  updateWorkspacePersonalSettings(body: { preferred_clone_protocol?: string; default_landing_page?: string }) { return this.request<WorkspacePersonalSettings>('/repos/workspace/settings/personal', { method: 'PATCH', body: JSON.stringify(body) }) }
  getWorkspaceOrganizationSettings() { return this.request<WorkspaceOrganizationSettings>('/repos/workspace/settings/organization') }
  updateWorkspaceOrganizationSettings(body: Partial<Omit<WorkspaceOrganizationSettings, 'tenant_id' | 'policy'>>) { return this.request<WorkspaceOrganizationSettings>('/repos/workspace/settings/organization', { method: 'PATCH', body: JSON.stringify(body) }) }
  getWorkspaceAccountContext() { return this.request<WorkspaceAccountContext>('/repos/workspace/account-context') }
  createWorkspaceAccountOrganization(body: { name: string; description?: string }) { return this.request<WorkspaceAccountContext>('/repos/workspace/account-context/organizations', { method: 'POST', body: JSON.stringify(body) }) }
  createWorkspaceAccountTeam(body: { organization_id: string; name: string; description?: string }) { return this.request<WorkspaceAccountContext>('/repos/workspace/account-context/teams', { method: 'POST', body: JSON.stringify(body) }) }
  createWorkspaceAccountInvitation(body: { email: string; role: string; organization_id?: string; team_id?: string }) { return this.request<WorkspaceAccountContext>('/repos/workspace/account-context/invitations', { method: 'POST', body: JSON.stringify(body) }) }
  getWorkspaceContextfulAccess() { return this.request<WorkspaceContextfulAccess>('/repos/workspace/contextful-access') }
  signupWorkspaceContextfulAccess() { return this.request<WorkspaceContextfulAccess>('/repos/workspace/contextful-access/signup', { method: 'POST' }) }
  recordWorkspaceContextfulUsage(body: { repo_id?: string; action?: string }) { return this.request<WorkspaceContextfulAccess>('/repos/workspace/contextful-access/usage', { method: 'POST', body: JSON.stringify(body) }) }
  createWorkspaceRepo(body: { name: string; description?: string; visibility?: string }) { return this.request<WorkspaceRepoSummary>('/repos/workspace', { method: 'POST', body: JSON.stringify(body) }) }
  forkWorkspaceRepo(body: { source_url: string; name: string; description?: string; visibility?: string }) { return this.request<WorkspaceRepoSummary>('/repos/workspace/fork', { method: 'POST', body: JSON.stringify(body) }) }
  previewWorkspaceRemoteImport(body: { source_url: string; name?: string; description?: string; visibility?: string; inspect_remote?: boolean }) { return this.request<WorkspaceImportPlan>('/repos/workspace/import-plan', { method: 'POST', body: JSON.stringify(body) }) }
  remoteImportWorkspaceRepo(body: { source_url: string; name: string; description?: string; visibility?: string; inspect_remote?: boolean }) { return this.request<WorkspaceRemoteImportResult>('/repos/workspace/remote-import', { method: 'POST', body: JSON.stringify(body) }) }
  async importWorkspaceRepo(body: { name: string; description?: string; visibility?: string; archive: File }) {
    const form = new FormData()
    form.set('name', body.name)
    form.set('description', body.description || '')
    form.set('visibility', body.visibility || 'private')
    form.set('archive', body.archive)
    const response = await fetch(`${API_BASE}/repos/workspace/import`, { method: 'POST', body: form, credentials: 'include' })
    if (!response.ok) throw new Error(await response.text() || `Request failed: ${response.status}`)
    return response.json() as Promise<WorkspaceRepoSummary>
  }
  getWorkspaceRepo(repoId: string) { return this.request<WorkspaceRepoDetail>(`/repos/workspace/${repoId}`) }
  getWorkspaceCodeIntelligence(repoId: string) { return this.request<WorkspaceCodeIntelligenceRecord>(`/repos/workspace/${repoId}/code-intelligence`) }
  listWorkspaceBranches(repoId: string) { return this.request<{ branches: string[] }>(`/repos/workspace/${repoId}/branches`) }
  createWorkspaceBranch(repoId: string, body: { branch_name: string; from_ref: string }) { return this.request<{ ok: boolean; branch: string; commit?: string }>(`/repos/workspace/${repoId}/branches`, { method: 'POST', body: JSON.stringify(body) }) }
  listWorkspaceCommits(repoId: string, limit = 20) { return this.request<{ commits: Array<Record<string, unknown>>; count: number }>(`/repos/workspace/${repoId}/commits?limit=${limit}`) }
  getWorkspaceCommitIntent(repoId: string, commitSha: string) { return this.request<WorkspaceCommitIntent>(`/repos/workspace/${repoId}/commits/${encodeURIComponent(commitSha)}/intent`) }
  listWorkspaceTree(repoId: string, ref = 'HEAD', path = '') { return this.request<{ entries: GitTreeEntry[] }>(`/repos/workspace/${repoId}/tree?${new URLSearchParams({ ref, path }).toString()}`) }
  readWorkspaceFile(repoId: string, filePath: string, ref = 'HEAD') { return this.request<{ content: string; path: string; size: number; truncated: boolean }>(`/repos/workspace/${repoId}/file?${new URLSearchParams({ ref, path: filePath }).toString()}`) }
  upsertWorkspaceFile(repoId: string, body: { path: string; content: string; ref: string; commit_message: string }) { return this.request<WorkspaceFileMutationResult>(`/repos/workspace/${repoId}/file`, { method: 'PUT', body: JSON.stringify(body) }) }
  deleteWorkspaceFile(repoId: string, body: { path: string; ref: string; commit_message: string }) { return this.request<WorkspaceFileMutationResult>(`/repos/workspace/${repoId}/file`, { method: 'DELETE', body: JSON.stringify(body) }) }
  diffWorkspaceRepo(repoId: string, base: string, head: string) { return this.request<{ diff: string }>(`/repos/workspace/${repoId}/diff?${new URLSearchParams({ base, head }).toString()}`) }
  listWorkspacePullRequests(repoId: string) { return this.request<WorkspacePullRequest[]>(`/repos/workspace/${repoId}/pull-requests`) }
  createWorkspacePullRequest(repoId: string, body: { title: string; description?: string; source_branch: string; target_branch: string }) { return this.request<WorkspacePullRequest>(`/repos/workspace/${repoId}/pull-requests`, { method: 'POST', body: JSON.stringify(body) }) }
  async getWorkspacePullRequestEvidence(repoId: string, prId: string) {
    const evidence = await this.request<Omit<WorkspacePullRequestEvidence, 'citations'>>(`/repos/workspace/${repoId}/pull-requests/${prId}/evidence`)
    return {
      ...evidence,
      citations: (evidence.canonical_citations || []).map(toCanonicalCitation),
    }
  }
  mergeWorkspacePullRequest(repoId: string, prId: string) { return this.request<WorkspacePullRequest>(`/repos/workspace/${repoId}/pull-requests/${prId}/merge`, { method: 'POST' }) }
  createWorkspaceBranchContextBundle(repoId: string, branchName: string, body: { purpose: string; task?: string; allowed_tools?: string[]; allowed_models?: string[] }) { return this.request<WorkspaceBranchContextBundle>(`/repos/workspace/${repoId}/branches/${encodeURIComponent(branchName)}/context-bundle`, { method: 'POST', body: JSON.stringify(body) }) }
  listWorkspaceSshKeys() { return this.request<WorkspaceSshKey[]>('/repos/workspace/ssh-keys') }
  createWorkspaceSshKey(body: { title: string; public_key: string }) { return this.request<WorkspaceSshKey>('/repos/workspace/ssh-keys', { method: 'POST', body: JSON.stringify(body) }) }
  deleteWorkspaceSshKey(keyId: string) { return this.request<void>(`/repos/workspace/ssh-keys/${keyId}`, { method: 'DELETE' }) }
  exportWorkspaceAuthorizedKeys() { return this.request<{ authorized_keys: string; count: number }>('/repos/workspace/ssh-keys/export') }
  getWorkspaceGitHttpCredentials() { return this.request<WorkspaceGitHttpCredential>('/repos/workspace/git-credentials') }
  rotateWorkspaceGitHttpCredentials() { return this.request<WorkspaceGitHttpCredential>('/repos/workspace/git-credentials/rotate', { method: 'POST' }) }
  getWorkspaceRepoSettings(repoId: string) { return this.request<WorkspaceRepoSettings>(`/repos/workspace/${repoId}/settings`) }
  updateWorkspaceRepoSettings(repoId: string, body: Partial<Omit<WorkspaceRepoSettings, 'repo_id' | 'name' | 'policy'>>) { return this.request<WorkspaceRepoSettings>(`/repos/workspace/${repoId}/settings`, { method: 'PATCH', body: JSON.stringify(body) }) }
  listWorkspaceRepoMembers(repoId: string) { return this.request<WorkspaceRepoMember[]>(`/repos/workspace/${repoId}/members`) }
  upsertWorkspaceRepoMember(repoId: string, body: { email: string; display_name?: string; role: WorkspaceRepoMember['role'] }) { return this.request<WorkspaceRepoMember[]>(`/repos/workspace/${repoId}/members`, { method: 'PUT', body: JSON.stringify(body) }) }
  deleteWorkspaceRepoMember(repoId: string, email: string) { return this.request<{ ok: boolean }>(`/repos/workspace/${repoId}/members?${new URLSearchParams({ email }).toString()}`, { method: 'DELETE' }) }
  listKnowledgeNodes(projectId: string, status?: string, nodeType?: string, limit = 100) {
    return this.request<{ nodes: ForgeKnowledgeNode[] }>('/knowledge/nodes', {
      method: 'POST',
      body: JSON.stringify({
        project_id: projectId,
        status,
        node_type: nodeType,
        limit,
      }),
    })
  }
  listKnowledgeEdges(projectId: string) {
    return this.request<{ edges: ForgeKnowledgeEdge[] }>(`/knowledge/projects/${projectId}/edges`)
  }
  linkKnowledgeNodes(projectId: string, sourceNodeId: string, targetNodeId: string, edgeType = 'relates_to', weight = 0.8, metadata?: Record<string, unknown>) {
    return this.request<{ ok: boolean; edge: ForgeKnowledgeEdge }>(`/knowledge/projects/${projectId}/link`, {
      method: 'POST',
      body: JSON.stringify({
        source_node_id: sourceNodeId,
        target_node_id: targetNodeId,
        edge_type: edgeType,
        weight,
        metadata,
      }),
    })
  }
}

const api = new ApiClient()
export default api
